import React, { useEffect, useState } from "react";
import {
  Switch,
  Form,
  Flex,
  Button,
  Col,
  Row,
  Modal,
  message,
  DatePicker,
  Spin,
} from "antd";
import { Input, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DeleteSVG from "../../assets/common/DeleteSVG";
import { queryKeys } from "../../services/react-query/queryKeys";
import {
  addGiftCard,
  getGiftCardDetails,
  updateGiftCard,
} from "../../network/giftCard";
import {
  addVoucherDuration,
  getVoucherDuration,
} from "../../network/voucherDuration";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

interface AddGiftCardFormProps {
  visible: boolean;
  onClose: () => void;
  id?: number;
}

const AddGiftCardForm: React.FC<AddGiftCardFormProps> = ({
  visible,
  onClose,
  id,
}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [selectedValue, setselectedValue] = useState<any>([]);
  const [voucherdurationsOpen, setVoucherDurationsOpen] =
    useState<boolean>(false);
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const { locationId } = useSelector(
    (state: { calendar: { locationId: number } }) => ({
      locationId: state.calendar.locationId,
    })
  );
  const salesTypes = [
    {
      key: "in_store_only",
      label: intl.formatMessage({ id: "in_store_only" }),
    },
    {
      key: "in_store_online",
      label: intl.formatMessage({ id: "in_store_online" }),
    },
  ];
  const handleRemoveValue = (indexToRemove: number) => {
    setselectedValue((prevData: any) =>
      prevData.filter((_: any, index: number) => index !== indexToRemove)
    );
  };

  const { data: listVoucherDurations, isLoading: isVoucherDurationsLoading } =
    useQuery([queryKeys.listVoucherDurations], () => getVoucherDuration());

  const { data: giftCardDetails, isLoading: isFetchingDetails } = useQuery(
    [queryKeys?.listGiftCards, id],
    () => getGiftCardDetails(locationId, id),
    {
      enabled: !!id && !!locationId,
      select: (res) => res?.data,
    }
  );

  const { mutate: onAddGiftCards, isLoading } = useMutation(
    (body: any) => addGiftCard(locationId, body),
    {
      onSuccess: (res: any) => {
        if (res?.response?.status === 422 || res?.response?.status === 500) {
          message.error(res?.response?.data?.message);
        } else {
          message.success(intl.formatMessage({ id: "giftCardAddedSuccess" }));

          queryClient.invalidateQueries([queryKeys.listGiftCards]);
          onClose();
        }
      },
      onError: (error) => {
        console.log("Add Gift Card error:", error);
        message.error("حاول مرة اخرى");
      },
    }
  );
  const { mutate: onUpdateGiftCards, isLoading: isUpdating } = useMutation(
    (body: any) => updateGiftCard(locationId, id, body),
    {
      onSuccess: (res: any) => {
        if (res?.response?.status === 422 || res?.response?.status === 500) {
          message.error(res?.response?.data?.message);
        } else {
          message.success(intl.formatMessage({ id: "giftCardUpdatedSuccess" }));

          queryClient.invalidateQueries([queryKeys.listGiftCards]);
          onClose();
        }
      },
      onError: (error) => {
        console.log("Add Gift Card error:", error);
        message.error("حاول مرة اخرى");
      },
    }
  );

  const { mutate: onAddVoucherDurations, isLoading: isLoadingVoucher } =
    useMutation(addVoucherDuration, {
      onSuccess: (res: any) => {
        if (res?.response?.status === 422 || res?.response?.status === 500) {
          message.error(res?.response?.data?.message);
        } else {
          console.log(res?.data);
          form.setFieldValue(
            "voucher_duration_id",
            res?.data?.data[0]?.id || null
          );
          message.success("Voucher Duration created successfully");
          setVoucherDurationsOpen(false);
          queryClient.invalidateQueries([queryKeys.listVoucherDurations]);
        }
      },
      onError: (error) => {
        console.log("Add Gift Card error:", error);
        message.error("حاول مرة اخرى");
      },
    });

  const onFinish = async (values: any) => {
    let extractedValues = selectedValue?.map((item: any) =>
      parseInt(item.value)
    );
    if (extractedValues?.length === 0) {
      message.error("Please add at least one value");
      return;
    }
    values.values = extractedValues;
    if (giftCardDetails) {
      onUpdateGiftCards(values);
    } else {
      onAddGiftCards(values);
    }
  };

  useEffect(() => {
    // Set initial values from giftCards?.values to selectedValue state
    if (giftCardDetails?.values) {
      setselectedValue(
        giftCardDetails.values.map((item: { id: number; value: string }) => ({
          value: item?.value,
        }))
      );
    }
  }, [giftCardDetails?.values]);

  const handleAddVoucherDurations = (values: any) => {
    const body = {
      name: {
        ar: values?.name?.ar,
        en: values?.name?.ar,
      },
      start_date: dayjs(values?.voucherdurations?.[0]).format("YYYY-MM-DD"),
      end_date: dayjs(values?.voucherdurations?.[1]).format("YYYY-MM-DD"),

      //   ...values,
      //   start_date: dayjs(values?.voucherdurations?.[0]).format('YYYY-MM-DD'),
      //   end_date: dayjs(values?.voucherdurations?.[1]).format('YYYY-MM-DD'),
    };
    onAddVoucherDurations(body);
  };
  const disabledDate = (current: any) => {
    return current && current < dayjs().startOf("day");
  };
  const allowCustomGiftCard = Form.useWatch("allow_custom_gift_card", form);

  return (
    <>
      <Modal
        centered
        open={visible}
        onCancel={onClose}
        okButtonProps={{ disabled: true }}
        cancelButtonProps={{ disabled: true }}
        footer={[]}
        width={1100}
        destroyOnClose
      >
        <div>
          <div className="text-color-primary text-2xl font-semibold leading-7 text-center">
            {giftCardDetails
              ? intl.formatMessage({ id: "edit_gift_card_title" })
              : intl.formatMessage({ id: "add_gift_card_title" })}
          </div>

          <div className="my-4 text-color-gray text-base font-normal leading-7 text-center">
            {giftCardDetails
              ? intl.formatMessage({ id: "edit_gift_card_description" })
              : intl.formatMessage({ id: "add_gift_card_description" })}
          </div>
          {isFetchingDetails && id !== undefined ? (
            <div className="flex justify-center items-center h-full">
              <Spin size="large" />
            </div>
          ) : (
            <Form
              layout="vertical"
              onFinish={onFinish}
              initialValues={{
                allow_custom_gift_card:
                  giftCardDetails?.allow_custom_gift_card || false,
                enable_gift_card: giftCardDetails?.enable_gift_card || false,
                enable_design: giftCardDetails?.enable_design || false,
                available_sale: giftCardDetails?.available_sale || "",
                voucher_duration_id: giftCardDetails?.voucher_duration_id || "",
                min_value: giftCardDetails?.min_value || "",
                max_value: giftCardDetails?.max_value || "",
                values: [{}],
              }}
              form={form}
            >
              <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
                <Col
                  className="gutter-row"
                  xl={12}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={"available_sale"}
                    label={intl.formatMessage({ id: "available_in_label" })}
                    required={false}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "available_sale_required_message",
                        }),
                      },
                      {
                        type: "string",
                        min: 2,
                        message: intl.formatMessage({
                          id: "name_length_message",
                        }),
                      },
                    ]}
                  >
                    <Select
                      placeholder={intl.formatMessage({
                        id: "available_in_placeholder",
                      })}
                      id="input-with-icon-adornment"
                      options={salesTypes?.map((item) => ({
                        value: item?.key,
                        label: (
                          <span className="capitalize">{item?.label}</span>
                        ),
                      }))}
                      loading={isVoucherDurationsLoading}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={12}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
                    <Col span={24}>
                      <Form.Item
                        label={intl.formatMessage({
                          id: "voucher_duration_label",
                        })}
                        name={"voucher_duration_id"}
                        required={false}
                        rules={[
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: "voucher_duration_required_message",
                            }),
                          },
                        ]}
                      >
                        <Select
                          placeholder={intl.formatMessage({
                            id: "select_duration_placeholder",
                          })}
                          id="input-with-icon-adornment"
                          dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Button
                                onClick={() => setVoucherDurationsOpen(true)}
                                icon={<PlusOutlined />}
                                type="primary"
                                className="w-full btn-primary-light"
                                style={{ justifyContent: "flex-start" }}
                                size="small"
                              >
                                {intl.formatMessage({
                                  id: "add_duration_button",
                                })}
                              </Button>
                            </>
                          )}
                          options={listVoucherDurations?.data.map(
                            (item: any) => ({
                              value: item.id,
                              label: item.name,
                            })
                          )}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <div className="flex justify-between gap-4 items-baseline ">
                <div className="">
                  <label
                    htmlFor="enable_gift_card"
                    className=" font-normal  w-full "
                  >
                    {intl.formatMessage({ id: "enable_gift_card_label" })}
                  </label>
                </div>
                <div>
                  <Form.Item
                    id="enable_gift_card"
                    name="enable_gift_card"
                    valuePropName="checked"
                    className=""
                  >
                    <Switch />
                  </Form.Item>
                </div>
              </div>

              <Form.List name="values">
                {(fields, { add, remove }) => (
                  <>
                    <Form.Item className="p-2  rounded bg-color-light-gr w-full text-color-primary">
                      <Row className="mb-4">
                        <Col
                          className="gutter-row"
                          xl={24}
                          lg={8}
                          xs={24}
                          sm={24}
                          span={11}
                        >
                          <label className="text-color-primary">
                            <FormattedMessage id="gift_card_value_label" />
                          </label>
                        </Col>
                      </Row>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <div
                          className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-2"
                          key={key}
                        >
                          {selectedValue?.map((value: any, index: any) => (
                            <div className="">
                              <Form.Item required={false} label=" ">
                                <Input
                                  placeholder={intl.formatMessage({
                                    id: "enter_value_placeholder",
                                  })}
                                  className="w-full my-input bg-[#EEF0FF] cursor-auto"
                                  bordered={false}
                                  defaultValue={value?.value}
                                  disabled
                                  suffix={
                                    <div
                                      onClick={() => handleRemoveValue(index)}
                                    >
                                      <DeleteSVG />
                                    </div>
                                  }
                                />
                              </Form.Item>
                            </div>
                          ))}

                          {selectedValue?.length < 5 && (
                            <div className="col-span-2 flex gap-2">
                              <Col
                                className="gutter-row"
                                xl={8}
                                lg={12}
                                xs={24}
                                sm={12}
                              >
                                <Form.Item
                                  {...restField}
                                  required={false}
                                  rules={[
                                    {
                                      validator(rule, value, callback) {
                                        if (value <= 0) {
                                          return Promise.reject(
                                            intl.formatMessage({
                                              id: "value_error_message",
                                            })
                                          );
                                        } else {
                                          return Promise.resolve();
                                        }
                                      },
                                    },
                                  ]}
                                  name={[name, "value"]}
                                  label={
                                    <FormattedMessage id="gift_card_value_label" />
                                  }
                                >
                                  <Input
                                    type="number"
                                    min={giftCardDetails?.min_value || 0}
                                    max={giftCardDetails?.max_value || 100}
                                    placeholder={intl.formatMessage({
                                      id: "enter_value_placeholder",
                                    })}
                                    className="w-full my-input"
                                  />
                                </Form.Item>
                              </Col>

                              <Col
                                className="gutter-row"
                                xl={1}
                                lg={12}
                                xs={24}
                                sm={12}
                                span={12}
                              >
                                <Form.Item label=" ">
                                  <Button
                                    className="border-none shadow-none outline-none"
                                    onClick={() => {
                                      if (selectedValue.length < 5) {
                                        const pricingData = form.getFieldValue([
                                          "values",
                                          index,
                                        ]);

                                        if (
                                          !pricingData ||
                                          !pricingData.value
                                        ) {
                                          message.error(
                                            intl.formatMessage({
                                              id: "value_empty_error",
                                            })
                                          );
                                          return;
                                        }

                                        if (pricingData?.value <= 0) {
                                          message.error(
                                            intl.formatMessage({
                                              id: "value_error_message",
                                            })
                                          );

                                          return;
                                        }

                                        if (
                                          selectedValue.some(
                                            (item: any) =>
                                              +item.value === +pricingData.value
                                          )
                                        ) {
                                          message.error(
                                            intl.formatMessage({
                                              id: "value_added_already_error",
                                            })
                                          );
                                          return;
                                        }

                                        if (selectedValue.length >= 4) {
                                          message.error(
                                            intl.formatMessage({
                                              id: "maximum_reached_message",
                                            })
                                          );
                                        }

                                        setselectedValue((prevIds: any) => [
                                          ...prevIds,
                                          pricingData,
                                        ]);
                                        add();
                                        remove(index);
                                      } else {
                                        message.error(
                                          intl.formatMessage({
                                            id: "maximum_reached_message",
                                          })
                                        );
                                      }
                                    }}
                                    block
                                    icon={
                                      <PlusOutlined className="bg-[#00086D] text-white p-1 rounded-md" />
                                    }
                                  />
                                </Form.Item>
                              </Col>
                            </div>
                          )}
                        </div>
                      ))}
                    </Form.Item>
                  </>
                )}
              </Form.List>
              <div className="flex justify-between items-baseline">
                <div>
                  <label
                    htmlFor="allow_custom_gift_card"
                    className="text-right text-lg font-normal "
                  >
                    <FormattedMessage id="enable_custom_gift_cards_label" />
                  </label>
                </div>

                <div>
                  <Form.Item
                    id="allow_custom_gift_card"
                    name="allow_custom_gift_card"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </div>
              </div>

              {allowCustomGiftCard && (
                <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
                  <Col
                    className="gutter-row"
                    xl={6}
                    lg={12}
                    md={12}
                    xs={24}
                    sm={8}
                    span={11}
                  >
                    <Form.Item
                      name={"min_value"}
                      label={intl.formatMessage({ id: "min_value_label" })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: "value_required_message",
                          }),
                        },
                        {
                          validator(rule, value, callback) {
                            if (value <= 0) {
                              return Promise.reject(
                                intl.formatMessage({
                                  id: "value_greater_than_zero_message",
                                })
                              );
                            } else {
                              return Promise.resolve();
                            }
                          },
                        },
                      ]}
                      required={false}
                    >
                      <Input
                        type="number"
                        placeholder={intl.formatMessage({
                          id: "min_value_label",
                        })}
                        className="w-full"
                        id="input-with-icon-adornment"
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    className="gutter-row"
                    xl={6}
                    lg={12}
                    md={12}
                    xs={24}
                    sm={8}
                    span={11}
                  >
                    <Form.Item
                      label={intl.formatMessage({ id: "max_value_label" })}
                      name={"max_value"}
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: "value_required_message",
                          }),
                        },
                        {
                          validator(rule, value, callback) {
                            if (value <= 0) {
                              return Promise.reject(
                                intl.formatMessage({
                                  id: "value_greater_than_zero_message",
                                })
                              );
                            } else {
                              return Promise.resolve();
                            }
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "max_value_label",
                        })}
                        type="number"
                        className="w-full"
                        id="input-with-icon-adornment"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Flex gap="small" align="center" justify="center">
                <Button
                  type="primary"
                  block
                  loading={isLoading || isUpdating}
                  // className="bg-color-primary h-full text-white text-lg rounded-md px-2 w-full"
                  htmlType="submit"
                >
                  <FormattedMessage
                    id={giftCardDetails && id ? "save" : "add"}
                  />
                </Button>
                <Button
                  type="primary"
                  onClick={onClose}
                  disabled={isLoading}
                  block
                  className="btn-primary-light"
                  htmlType="button"
                >
                  <FormattedMessage id="cancel_button" />
                </Button>
              </Flex>
            </Form>
          )}
        </div>
      </Modal>
      <Modal
        centered
        width={700}
        title={intl.formatMessage({ id: "giftCardsDuration" })}
        open={voucherdurationsOpen}
        onCancel={() => setVoucherDurationsOpen(false)}
        footer={null}
      >
        <Form onFinish={handleAddVoucherDurations} layout="vertical">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name={["name", "ar"]}
              label={intl.formatMessage({ id: "durationName" })}
              required={false}
              // className="form-style "
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: "enterDurationName" }),
                },
                {
                  type: "string",
                  min: 3,
                  message: intl.formatMessage({ id: "nameGt3Chars" }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({ id: "enterDurationName" })}
              />
            </Form.Item>

            <Form.Item
              required={false}
              label={intl.formatMessage({ id: "giftCardsDuration" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: "enterDurationDate" }),
                },
              ]}
              name="voucherdurations"
            >
              <RangePicker
                disabledDate={disabledDate}
                placeholder={[
                  intl.formatMessage({ id: "from_date" }),
                  intl.formatMessage({ id: "to_date" }),
                ]}
              />
            </Form.Item>
          </div>

          <Flex gap="small" align="center" justify="center">
            <Button
              type="primary"
              loading={isLoadingVoucher}
              block
              htmlType="submit"
            >
              {id && giftCardDetails
                ? intl.formatMessage({ id: "save" })
                : intl.formatMessage({ id: "add" })}
            </Button>
            <Button
              type="primary"
              block
              onClick={() => setVoucherDurationsOpen(false)}
              ghost
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};

export default AddGiftCardForm;
